import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/db';
import { logger } from '../../services/logger';
import { 
  Archive, 
  Calendar, 
  DollarSign, 
  Trophy, 
  Loader2, 
  AlertCircle,
  Eye,
  RefreshCw,
  Trash2
} from 'lucide-react';

interface ArchivedCompetition {
  id: string;
  promo_name: string;
  starting_balance: number;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  is_archived: boolean;
  archived_at: string | null;
  archived_by_email: string | null;
  created_at: string;
  updated_at: string;
}

export function HistoricalCompetitions() {
  const [competitions, setCompetitions] = useState<ArchivedCompetition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isUnarchiving, setIsUnarchiving] = useState<string | null>(null);

  useEffect(() => {
    fetchArchivedCompetitions();
  }, []);

  const fetchArchivedCompetitions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase.rpc('get_archived_competitions', {
        p_limit: 50,
        p_offset: 0
      });

      if (fetchError) throw fetchError;
      setCompetitions(data || []);
    } catch (err) {
      logger.error('Error fetching archived competitions:', err);
      setError('Failed to load archived competitions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnarchive = async (competitionId: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to unarchive this competition? This will make it visible in the historical list but will not reactivate it.'
    );

    if (!confirmed) return;

    try {
      setIsUnarchiving(competitionId);
      setError(null);
      setSuccess(null);

      const { data, error: unarchiveError } = await supabase.rpc('admin_unarchive_competition', {
        p_competition_id: competitionId
      });

      if (unarchiveError) throw unarchiveError;
      
      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to unarchive competition');
      }

      setSuccess('Competition unarchived successfully');
      await fetchArchivedCompetitions();
    } catch (err) {
      logger.error('Error unarchiving competition:', err);
      setError(err instanceof Error ? err.message : 'Failed to unarchive competition');
    } finally {
      setIsUnarchiving(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return `R${amount.toLocaleString()}`;
  };

  const getDuration = (start: string | null, end: string | null) => {
    if (!start || !end) return 'N/A';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-wayja-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-gray-100 p-3 rounded-full">
            <Archive className="w-6 h-6 text-gray-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Historical Competitions</h2>
            <p className="text-gray-500 text-sm">
              View and manage archived competitions
            </p>
          </div>
        </div>
        <button
          onClick={fetchArchivedCompetitions}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}
      
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-600">
          {success}
        </div>
      )}

      {/* Competitions List */}
      {competitions.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <Archive className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Archived Competitions</h3>
          <p className="text-gray-500">
            Archived competitions will appear here for historical reference.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {competitions.map((competition) => (
            <div
              key={competition.id}
              className="bg-white rounded-lg shadow-md p-6 border-l-4 border-gray-400"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Trophy className="w-5 h-5 text-gray-500" />
                    <h3 className="text-xl font-semibold text-gray-900">
                      {competition.promo_name}
                    </h3>
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                      Archived
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-gray-500">Start Date</p>
                        <p className="font-medium text-gray-900">{formatDate(competition.start_date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-gray-500">End Date</p>
                        <p className="font-medium text-gray-900">{formatDate(competition.end_date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-gray-500">Starting Balance</p>
                        <p className="font-medium text-gray-900">{formatCurrency(competition.starting_balance)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                    <span>Duration: {getDuration(competition.start_date, competition.end_date)}</span>
                    {competition.archived_at && (
                      <span>Archived: {formatDate(competition.archived_at)}</span>
                    )}
                    {competition.archived_by_email && (
                      <span>By: {competition.archived_by_email}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleUnarchive(competition.id)}
                    disabled={isUnarchiving === competition.id}
                    className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors flex items-center gap-2"
                    title="Unarchive this competition"
                  >
                    {isUnarchiving === competition.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    Unarchive
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">About Archived Competitions</p>
            <p>
              Archived competitions are kept for historical reference. You can view past competition details,
              settings, and dates. Unarchiving a competition will make it visible again but will not reactivate it.
              To reactivate a competition, you'll need to update its settings and set it as active.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
